// Turnover Package — /turnover/:turnoverID/package (2026-09-24).
// The package, read in the Turnover Engine instead of as an email attachment:
// the same content as the printed package (buildTurnoverPackageHtml, app view
// — no Section Status table), worked down section by section. Clicking a
// document opens it in a flyout (SharePoint preview via Graph) with Prev /
// Next across every document, so a reader can go boom, boom, boom through it.
// The package email's "Open in Turnover Engine" button links here.
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { sqlReadRaw } from "../utils/sqlClient";
import { getMailToken } from "../utils/msalHelpers";
import { loginScopes } from "../auth/msalConfig";
import { buildTurnoverPackageHtml } from "../utils/turnoverPrint";
import { parsePackageRow, shareIdForUrl } from "./turnover/packageData";

const GRAPH = "https://graph.microsoft.com/v1.0";
const fmtDate = (v) => (v ? new Date(String(v).slice(0, 10) + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

export default function TurnoverPackage() {
  const { turnoverID } = useParams();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [docId, setDocId] = useState(null);   // open flyout
  const [preview, setPreview] = useState({ state: "idle", url: "" });
  const [frameHeight, setFrameHeight] = useState(1200);
  const frameRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const id = parseInt(turnoverID, 10);
        const [detail, opts] = await Promise.all([
          sqlReadRaw(`EXEC sp_GetTurnoverDetail @turnover_id=${id}`),
          sqlReadRaw("EXEC sp_GetTurnoverConfigOptions").catch(() => null),
        ]);
        const parsed = parsePackageRow(detail?.Table1?.[0], { optionRows: opts?.Table1 || [] });
        if (!parsed) throw new Error("Turnover not found.");
        if (alive) setData(parsed);
      } catch (e) {
        if (alive) setError(e.message || String(e));
      }
    })();
    return () => { alive = false; };
  }, [turnoverID]);

  const html = useMemo(() => (data ? buildTurnoverPackageHtml(data, { view: "app" }) : ""), [data]);
  // Documents in the order they appear in the package (for Prev / Next).
  const docOrder = useMemo(() => {
    if (!data) return [];
    const bySection = new Map(data.sections.map(s => [s.SectionID, s]));
    return data.documents
      .filter(d => d.SharePointUrl && bySection.get(d.SectionID)?.IsApplicable)
      .sort((a, b) => (bySection.get(a.SectionID).SectionNumber - bySection.get(b.SectionID).SectionNumber) || (a.TurnoverDocumentID - b.TurnoverDocumentID));
  }, [data]);
  const doc = docOrder.find(d => String(d.TurnoverDocumentID) === String(docId)) || null;
  const docIndex = doc ? docOrder.indexOf(doc) : -1;
  const docSection = doc ? data.sections.find(s => s.SectionID === doc.SectionID) : null;

  // Wire the package frame: size it to its content, catch document and section links.
  const onFrameLoad = useCallback(() => {
    const d = frameRef.current?.contentDocument;
    if (!d) return;
    setFrameHeight(d.documentElement.scrollHeight + 20);
    d.addEventListener("click", (e) => {
      const a = e.target.closest?.("a");
      if (!a) return;
      if (a.dataset.docId) { e.preventDefault(); setDocId(a.dataset.docId); return; }
      if (a.classList.contains("sec-open")) { e.preventDefault(); navigate(a.getAttribute("href")); }
    });
  }, [navigate]);

  // Fetch an embeddable preview for the open document.
  useEffect(() => {
    if (!doc) return;
    let alive = true;
    setPreview({ state: "loading", url: "" });
    (async () => {
      try {
        const token = (await getMailToken(instance, accounts, loginScopes)).accessToken;
        const h = { Authorization: `Bearer ${token}` };
        const item = await fetch(`${GRAPH}/shares/${shareIdForUrl(doc.SharePointUrl)}/driveItem?$select=id,parentReference`, { headers: h }).then(r => (r.ok ? r.json() : Promise.reject(new Error(`Graph ${r.status}`))));
        const pv = await fetch(`${GRAPH}/drives/${item.parentReference.driveId}/items/${item.id}/preview`, {
          method: "POST", headers: { ...h, "Content-Type": "application/json" }, body: "{}",
        }).then(r => (r.ok ? r.json() : Promise.reject(new Error(`Graph ${r.status}`))));
        if (alive) setPreview(pv?.getUrl ? { state: "ready", url: pv.getUrl } : { state: "failed", url: "" });
      } catch (e) {
        console.warn("[package] preview failed:", e.message);
        if (alive) setPreview({ state: "failed", url: "" });
      }
    })();
    return () => { alive = false; };
  }, [doc?.TurnoverDocumentID]); // eslint-disable-line react-hooks/exhaustive-deps

  const step = useCallback((dir) => {
    const next = docOrder[docIndex + dir];
    if (next) setDocId(String(next.TurnoverDocumentID));
  }, [docOrder, docIndex]);
  useEffect(() => {
    if (!doc) return;
    const onKey = (e) => {
      if (e.key === "Escape") setDocId(null);
      else if (e.key === "ArrowRight") step(1);
      else if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [doc, step]);

  if (error) return <div style={{ padding: 32, color: "var(--red)", fontSize: 13 }}>{error}</div>;
  if (!data) return <div style={{ padding: 32, color: "var(--text3)", fontSize: 13 }}>Loading the package…</div>;
  const t = data.turnover;
  const btn = { padding: "6px 14px", fontSize: 12.5, fontWeight: 700, borderRadius: 6, cursor: "pointer", border: "1px solid var(--cs-border)", background: "var(--surface)", color: "var(--text)" };

  return (
    <div style={{ background: "#f3f4f6", minHeight: "100%" }}>
      <div style={{ position: "sticky", top: 0, zIndex: 5, display: "flex", alignItems: "center", gap: 12, padding: "10px 24px", background: "var(--surface)", borderBottom: "1px solid var(--cs-border)" }}>
        <button onClick={() => navigate(`/turnover/${t.TurnoverID}`)} style={{ ...btn, border: "none", color: "var(--cs-blue)", padding: 0 }}>← {t.ProjectName}</button>
        <span style={{ fontSize: 12.5, color: "var(--text3)" }}>Turnover package — click any document to open it here; ← / → steps through them.</span>
        <span style={{ marginLeft: "auto" }} />
        {docOrder.length > 0 && <button style={btn} onClick={() => setDocId(String(docOrder[0].TurnoverDocumentID))}>▶ Step through documents ({docOrder.length})</button>}
        <button style={btn} onClick={() => frameRef.current?.contentWindow?.print()}>🖨 Print</button>
      </div>

      <iframe ref={frameRef} title="Turnover package" srcDoc={html} onLoad={onFrameLoad}
        sandbox="allow-same-origin allow-modals allow-popups allow-popups-to-escape-sandbox"
        style={{ display: "block", width: "100%", maxWidth: 1000, margin: "16px auto", height: frameHeight, border: "none", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,.08)" }} />

      {doc && (
        <>
          <div onClick={() => setDocId(null)} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.25)", zIndex: 1000 }} />
          <aside role="dialog" aria-label={doc.FileName} style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "min(62vw, 1100px)", minWidth: 420, background: "var(--surface)", zIndex: 1001, display: "flex", flexDirection: "column", boxShadow: "-6px 0 24px rgba(0,0,0,.18)" }}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--cs-border)", display: "flex", gap: 10, alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  §{docSection?.SectionNumber} {docSection?.SectionName} · {docIndex + 1} of {docOrder.length}
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.FileName}</div>
                <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 2 }}>
                  {[doc.DocType, fmtDate(doc.DocDate), doc.DocValue ? `$${Number(String(doc.DocValue).replace(/[$,]/g, "")).toLocaleString("en-US")}` : "", doc.UploadedBy && `uploaded by ${doc.UploadedBy}`].filter(Boolean).join(" · ")}
                </div>
                {doc.Description && <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 2 }}>{doc.Description}</div>}
              </div>
              <button style={btn} disabled={docIndex <= 0} onClick={() => step(-1)}>‹ Prev</button>
              <button style={btn} disabled={docIndex >= docOrder.length - 1} onClick={() => step(1)}>Next ›</button>
              <a href={doc.SharePointUrl} target="_blank" rel="noreferrer" style={{ ...btn, textDecoration: "none" }}>Open in SharePoint ↗</a>
              <button style={{ ...btn, border: "none", fontSize: 18, padding: "0 6px" }} aria-label="Close" onClick={() => setDocId(null)}>×</button>
            </div>
            <div style={{ flex: 1, background: "#f8f9fb", position: "relative" }}>
              {preview.state === "ready"
                ? <iframe key={preview.url} title={doc.FileName} src={preview.url} style={{ width: "100%", height: "100%", border: "none" }} />
                : <div style={{ padding: 32, fontSize: 13, color: "var(--text3)" }}>
                    {preview.state === "failed"
                      ? <>This file can't be previewed here. <a href={doc.SharePointUrl} target="_blank" rel="noreferrer">Open it in SharePoint ↗</a></>
                      : "Loading preview…"}
                  </div>}
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
